/**
 * Client für die persistente Bildspeicherung über REST.
 * Bilder werden auf dem Server in der Datenbank gehalten,
 * lokal werden nur Metadaten und IDs vorgehalten.
 */

import { fileToCompressedBase64 } from "@/lib/image-compress";

export interface ServerPhotoMetadata {
  id: number;
  projectId: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  hasAnnotation: boolean;
  description?: string | null;
  photoPositionen?: unknown;
  measurementData?: unknown;
  createdAt: string | null;
}

export interface PhotoMetadataPatch {
  /** Neuer Anzeigename des Fotos (Umbenennen). */
  fileName?: string;
  description?: string | null;
  photoPositionen?: unknown;
  measurementData?: unknown;
}

export const PhotoApi = {
  /** Liefert die Vorschau-URL für ein Foto. */
  getPhotoUrl(id: number | string): string {
    return `/api/photos/${id}`;
  },

  /** Liefert die annotierte Variante (mit Aufmaß-Overlay), Fallback auf Original. */
  getAnnotatedUrl(id: number | string): string {
    return `/api/photos/${id}/annotated`;
  },

  async listPhotos(projectId: number | string): Promise<ServerPhotoMetadata[]> {
    const res = await fetch(`/api/projects/${projectId}/photos`, { credentials: "include" });
    if (!res.ok) throw new Error("Fotos konnten nicht geladen werden");
    return await res.json();
  },

  async uploadPhotos(
    projectId: number | string,
    files: File[],
    options?: {
      onProgress?: (info: {
        completed: number;
        total: number;
        file: File;
        phase: "start" | "done";
      }) => void;
    },
  ): Promise<{
    successes: ServerPhotoMetadata[];
    failures: { file: File; error: string }[];
  }> {
    const successes: ServerPhotoMetadata[] = [];
    const failures: { file: File; error: string }[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      options?.onProgress?.({
        completed: i,
        total: files.length,
        file,
        phase: "start",
      });
      try {
        const meta = await this.uploadPhoto(projectId, file);
        successes.push(meta);
      } catch (err) {
        failures.push({
          file,
          error: err instanceof Error ? err.message : "Unbekannter Fehler",
        });
      }
      options?.onProgress?.({
        completed: i + 1,
        total: files.length,
        file,
        phase: "done",
      });
    }
    return { successes, failures };
  },

  async uploadPhoto(projectId: number | string, file: File): Promise<ServerPhotoMetadata> {
    // Vor dem Upload herunterskalieren / re-encodieren, damit die spätere
    // KI-Analyse das Bild auch unterhalb des 5-MiB-Anthropic-Limits sieht.
    const compressed = await fileToCompressedBase64(file);
    const res = await fetch(`/api/projects/${projectId}/photos`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileName: file.name,
        mimeType: compressed.mimeType || file.type || "application/octet-stream",
        fileSize: compressed.fileSize,
        data: compressed.data,
      }),
    });
    if (!res.ok) {
      const msg = await safeMessage(res);
      throw new Error(msg || "Foto-Upload fehlgeschlagen");
    }
    return await res.json();
  },

  async uploadDataUrl(
    projectId: number | string,
    dataUrl: string,
    fileName: string,
    fileSize?: number,
  ): Promise<ServerPhotoMetadata> {
    const { mimeType, base64 } = parseDataUrl(dataUrl);
    const size = fileSize ?? Math.floor((base64.length * 3) / 4);
    const res = await fetch(`/api/projects/${projectId}/photos`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileName, mimeType, fileSize: size, data: base64 }),
    });
    if (!res.ok) {
      const msg = await safeMessage(res);
      throw new Error(msg || "Foto-Upload fehlgeschlagen");
    }
    return await res.json();
  },

  async saveAnnotation(id: number | string, dataUrl: string | null): Promise<void> {
    const annotatedData = dataUrl ? parseDataUrl(dataUrl).base64 : null;
    const res = await fetch(`/api/photos/${id}/annotation`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ annotatedData }),
    });
    if (!res.ok) throw new Error("Annotation konnte nicht gespeichert werden");
  },

  async updateMetadata(id: number | string, patch: PhotoMetadataPatch): Promise<void> {
    const res = await fetch(`/api/photos/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error("Foto-Metadaten konnten nicht gespeichert werden");
  },

  async deletePhoto(id: number | string): Promise<void> {
    const res = await fetch(`/api/photos/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok && res.status !== 404) throw new Error("Foto konnte nicht gelöscht werden");
  },

  /** Lädt ein Foto als Blob (für PDF-Komprimierung etc.). */
  async fetchBlob(id: number | string, annotated = false): Promise<Blob> {
    const url = annotated ? this.getAnnotatedUrl(id) : this.getPhotoUrl(id);
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error("Foto konnte nicht geladen werden");
    return await res.blob();
  },
};


function parseDataUrl(dataUrl: string): { mimeType: string; base64: string } {
  const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
  if (!match) {
    return { mimeType: "image/jpeg", base64: dataUrl };
  }
  return { mimeType: match[1], base64: match[2] };
}

async function safeMessage(res: Response): Promise<string | null> {
  try {
    const j = await res.json();
    return j?.message ?? null;
  } catch {
    return null;
  }
}
